import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getUserData } from "../../../services/apiService";
import {
  fetchStudyPlaceholdersForGates,
  fetchStudyPlaceholdersForActiveSite,
  fetchStudySourceTemplate,
  enrichDocumentsWithPlaceholderLinks,
  consolidateDocumentSignatureRows,
  augmentPlaceholdersFromDocuments,
  dedupeStudyPlaceholdersForTree,
} from "./isfPlaceholderService";
import { buildSiteOverviewSnapshot } from "./isfSiteOverviewService";
import {
  fetchStudySitesForSetup,
  normalizeStudySiteRows,
  normalizeStudyFolderResponseData,
} from "./isfSiteSetupService";
import { fetchRoleLookup } from "../../../services/lookupService";
import { guidOrNull } from "../../../utils/guidUtils";

/** Default study gates (phases) — seeded per org in EISF_Gate. */
export const STUDY_GATES = [
  { code: "PRE_STUDY", name: "Pre-study", sortOrder: 1 },
  { code: "START_UP", name: "Start-up", sortOrder: 2 },
  { code: "CONDUCT", name: "Conduct", sortOrder: 3 },
  { code: "CLOSE_OUT", name: "Close-out", sortOrder: 4 },
  { code: "POST_STUDY", name: "Post-study", sortOrder: 5 },
];

/** Unique gate colors used across all table/badge views. */
export const STUDY_GATE_COLORS = {
  PRE_STUDY: "#4f6b95",
  START_UP: "#0f766e",
  CONDUCT: "#15803d",
  CLOSE_OUT: "#c2410c",
  POST_STUDY: "#6d28d9",
  DEFAULT: "#64748b",
};

export const STUDY_GATE_BADGE_CLASS = {
  PRE_STUDY: "pre-study",
  START_UP: "start-up",
  CONDUCT: "conduct",
  CLOSE_OUT: "close-out",
  POST_STUDY: "post-study",
  DEFAULT: "default",
};

export const normalizeGateCode = (code) =>
  String(code || "PRE_STUDY")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

export const getStudyGateColor = (code) => {
  const normalized = normalizeGateCode(code);
  return STUDY_GATE_COLORS[normalized] || STUDY_GATE_COLORS.DEFAULT;
};

export const getStudyGateBadgeClass = (code) => {
  const normalized = normalizeGateCode(code);
  const suffix = STUDY_GATE_BADGE_CLASS[normalized] || STUDY_GATE_BADGE_CLASS.DEFAULT;
  return `study-gate-badge study-gate-badge--${suffix}`;
};

export const STUDY_GATE_OPTIONS = STUDY_GATES.map((g) => ({
  value: g.code,
  label: g.name,
}));

export const gateNameForCode = (code) => {
  const normalized = normalizeGateCode(code);
  const match = STUDY_GATES.find((g) => g.code === normalized);
  return match?.name || String(code || "").trim() || "";
};

export const HISTORICAL_GATE_READONLY_MESSAGE =
  "You are viewing a different study phase. Switch to the current phase to upload documents or make changes.";

export const isViewingHistoricalStudyGate = (viewGateCode, actualGateCode) => {
  const actual = normalizeGateCode(actualGateCode || "PRE_STUDY");
  const viewing = normalizeGateCode(viewGateCode || actual);
  return viewing !== actual;
};

const resolveSessionUser = (userData) => {
  if (userData?.OrganizationId && userData?.StaffId) return userData;
  const session = getUserData();
  return userData?.OrganizationId ? userData : session || userData || {};
};

const basePayload = (userData, overrides = {}) => {
  const session = resolveSessionUser(userData);
  return {
    OrganizationIdRef: session.OrganizationId || session.organizationId || "",
    CreatedBy: session.StaffId || session.staffId || "",
    ActionType: "",
    ...overrides,
  };
};

function rowHasColumns(row, requiredColumns = []) {
  if (!row || typeof row !== "object") return false;
  const keys = new Set(
    Object.keys(row).map((key) => String(key).trim().toLowerCase()),
  );
  return requiredColumns.every((col) => keys.has(String(col).trim().toLowerCase()));
}

function collectResponseTables(data) {
  if (!data || typeof data !== "object") return [];
  const tables = [];
  if (Array.isArray(data.Tables)) {
    data.Tables.forEach((table) => {
      const rows = table?.Rows || table?.rows;
      if (Array.isArray(rows) && rows.length) tables.push(rows);
    });
  }
  Object.keys(data).forEach((key) => {
    if (key === "Tables" || key === "tables") return;
    const value = data[key];
    if (Array.isArray(value) && value.length && typeof value[0] === "object") {
      tables.push(value);
    }
  });
  if (Array.isArray(data) && data.length && typeof data[0] === "object") {
    tables.push(data);
  }
  return tables;
}

function findRowsByColumns(response, requiredColumns = []) {
  const data = response?.data;
  if (!data) return [];
  for (const rows of collectResponseTables(data)) {
    if (rows.length && rowHasColumns(rows[0], requiredColumns)) return rows;
  }
  return [];
}

function extractDataTableRows(response, tableName) {
  const data = response?.data;
  if (!data) return [];

  const tryArray = (value) => (Array.isArray(value) ? value : null);
  const target = String(tableName || "").toLowerCase();

  if (Array.isArray(data)) return data;

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (key === "Tables") continue;
      if (String(key).toLowerCase() === target) {
        const rows = tryArray(data[key]);
        if (rows) return rows;
      }
    }

    const direct =
      tryArray(data[tableName]) ??
      tryArray(data[tableName?.toLowerCase?.()]) ??
      tryArray(data[tableName?.toUpperCase?.()]);
    if (direct) return direct;
  }

  if (Array.isArray(data?.Tables)) {
    const named = data.Tables.find(
      (t) =>
        String(t?.TableName || t?.Name || t?.tableName || "").toLowerCase() === target,
    );
    if (Array.isArray(named?.Rows)) return named.Rows;
    if (data.Tables.length === 1) {
      const first = data.Tables[0]?.Rows;
      if (Array.isArray(first)) return first;
    }
  }

  return tryArray(data.Table) ?? tryArray(data.table) ?? [];
}

export const normalizeStudyGateState = (response) => {
  const rows = extractDataTableRows(response, "StudyGate");
  let row = rows.find(
    (item) =>
      item?.CurrentGateCode ||
      item?.currentGateCode ||
      item?.StudyGateStateId ||
      item?.studyGateStateId,
  );
  if (!row) row = rows[0];

  const data = response?.data;
  if (!row && data?.Tables && Array.isArray(data.Tables)) {
    for (const table of data.Tables) {
      const tableRows = table?.Rows || table?.rows || [];
      const match = tableRows.find(
        (item) => item?.CurrentGateCode || item?.currentGateCode,
      );
      if (match) {
        row = match;
        break;
      }
    }
  }

  if (!row) return null;
  const currentGateCode =
    row.CurrentGateCode ?? row.currentGateCode ?? row.GateCode ?? row.gateCode ?? "";
  const currentGateName =
    row.CurrentGateName ?? row.currentGateName ?? row.GateName ?? row.gateName ?? "";
  return {
    ...row,
    CurrentGateCode: currentGateCode,
    CurrentGateName: currentGateName,
  };
};

function extractGateCatalogRows(response) {
  const data = response?.data;
  if (!data) return [];

  const hasGateCatalogColumns = (row) => {
    if (!row || typeof row !== "object") return false;
    const keys = new Set(
      Object.keys(row).map((key) => String(key).trim().toLowerCase()),
    );
    return keys.has("gatecode") && keys.has("sortorder");
  };

  const tryRows = (rows) =>
    Array.isArray(rows) && rows.length && hasGateCatalogColumns(rows[0]) ? rows : null;

  const named = extractDataTableRows(response, "GateList");
  const fromNamed = tryRows(named);
  if (fromNamed) return fromNamed;

  const tables = data?.Tables;
  if (Array.isArray(tables)) {
    for (const table of tables) {
      const rows = table?.Rows || table?.rows || [];
      const matched = tryRows(rows);
      if (matched) return matched;
    }
  }

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (key === "Tables") continue;
      const matched = tryRows(data[key]);
      if (matched) return matched;
    }
  }

  return [];
}

/** Always returns all 5 study phases; merges org-specific names from API when present. */
export const buildStudyGateOptions = (gateList = []) => {
  const byCode = new Map(STUDY_GATES.map((gate) => [gate.code, { ...gate }]));

  const addRow = (row) => {
    const code = normalizeGateCode(
      row?.code ??
        row?.GateCode ??
        row?.gateCode ??
        row?.CurrentGateCode ??
        row?.currentGateCode ??
        "",
    );
    if (!code) return;

    const existing = byCode.get(code) || {
      code,
      name: gateNameForCode(code),
      sortOrder: 99,
    };

    byCode.set(code, {
      code,
      name:
        String(
          row?.name ??
            row?.GateName ??
            row?.gateName ??
            row?.CurrentGateName ??
            row?.currentGateName ??
            "",
        ).trim() ||
        existing.name ||
        gateNameForCode(code),
      sortOrder: Number(
        row?.sortOrder ?? row?.SortOrder ?? existing.sortOrder ?? 0,
      ),
    });
  };

  (gateList || []).forEach(addRow);
  return Array.from(byCode.values()).sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
};

/** Current phase index + catalog for progress strip / picker. */
export const buildStudyGateProgress = (gateList = [], studyGate = null) => {
  const gates = buildStudyGateOptions(gateList);
  const actualStudyGateCode = normalizeGateCode(
    studyGate?.CurrentGateCode ||
      studyGate?.currentGateCode ||
      "PRE_STUDY",
  );
  const currentIndex = gates.findIndex(
    (gate) => normalizeGateCode(gate.code) === actualStudyGateCode,
  );
  return {
    gates,
    currentIndex: currentIndex >= 0 ? currentIndex : 0,
    actualStudyGateCode,
  };
};

export const normalizeGateList = (response) => {
  const catalogRows = extractGateCatalogRows(response);
  if (catalogRows.length) {
    return buildStudyGateOptions(
      catalogRows.map((row) => ({
        code: row.GateCode ?? row.gateCode ?? "",
        name: row.GateName ?? row.gateName ?? "",
        sortOrder: row.SortOrder ?? row.sortOrder ?? 0,
      })),
    );
  }

  const fallbackRows = extractDataTableRows(response, "GateList");
  if (fallbackRows.length) {
    return buildStudyGateOptions(fallbackRows);
  }

  return buildStudyGateOptions(STUDY_GATES);
};

export const fetchStudyGate = async ({ userData, studyId }) => {
  const response = await apiClient.post(
    API_ENDPOINTS.ISF.STUDY_GATE,
    basePayload(userData, {
      ActionType: "GetStudyGate",
      StudyIdRef: studyId,
    }),
  );
  return {
    response,
    state: normalizeStudyGateState(response),
    gates: normalizeGateList(response),
  };
};

export const ensureStudyGate = async ({ userData, studyId }) =>
  apiClient.post(
    API_ENDPOINTS.ISF.STUDY_GATE,
    basePayload(userData, {
      ActionType: "EnsureStudyGate",
      StudyIdRef: studyId,
    }),
  );

export const tryAdvanceStudyGate = async ({ userData, studyId }) => {
  const response = await apiClient.post(
    API_ENDPOINTS.ISF.STUDY_GATE,
    basePayload(userData, {
      ActionType: "TryAdvanceStudyGate",
      StudyIdRef: studyId,
    }),
  );
  const row = extractDataTableRows(response, "GateAdvance")[0] || null;
  return { response, result: row };
};

export const normalizeGateAdvanceSummary = (response) => {
  const named = extractDataTableRows(response, "GateAdvanceSummary")[0];
  if (named && rowHasColumns(named, ["CanAdvance", "CurrentGateCode"])) return named;
  return findRowsByColumns(response, ["CanAdvance", "CurrentGateCode", "NextGateCode"])[0] || null;
};

export const normalizeGateAdvanceSiteReadiness = (response) => {
  const named = extractDataTableRows(response, "GateAdvanceSiteReadiness");
  if (named.length && rowHasColumns(named[0], ["SiteId", "PendingCount"])) return named;
  return findRowsByColumns(response, ["SiteId", "PendingCount", "IsReady"]);
};

export const fetchStudyGateAdvanceReadiness = async ({ userData, studyId }) => {
  const response = await apiClient.post(
    API_ENDPOINTS.ISF.STUDY_GATE,
    basePayload(userData, {
      ActionType: "GetStudyGateAdvanceReadiness",
      StudyIdRef: studyId,
    }),
  );
  return {
    response,
    summary: normalizeGateAdvanceSummary(response),
    sites: normalizeGateAdvanceSiteReadiness(response),
  };
};

export const isGateAdvanceAllowed = (summary) =>
  summary?.CanAdvance === 1 ||
  summary?.CanAdvance === true ||
  summary?.canAdvance === 1 ||
  summary?.canAdvance === true;

/** How a phase picker selection should behave (view history vs advance vs blocked skip). */
export function getGatePickerSelectionMode(targetCode, actualCode, gateList = []) {
  const gates = buildStudyGateOptions(gateList);
  const actual = normalizeGateCode(actualCode || "PRE_STUDY");
  const target = normalizeGateCode(targetCode || actual);
  const actualSort = gates.find((g) => g.code === actual)?.sortOrder ?? 0;
  const targetSort = gates.find((g) => g.code === target)?.sortOrder ?? 0;

  if (target === actual) return "current";
  if (targetSort < actualSort) return "view";
  if (targetSort === actualSort + 1) return "advance";
  return "blocked";
}

export function getNextGateOption(gateList = [], actualCode) {
  const gates = buildStudyGateOptions(gateList);
  const actual = normalizeGateCode(actualCode || "PRE_STUDY");
  const actualSort = gates.find((g) => g.code === actual)?.sortOrder ?? 0;
  return gates.find((g) => g.sortOrder === actualSort + 1) || null;
}

export const DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY = "eISF";

function sortGateAdvanceTasks(tasks = []) {
  return [...tasks].sort((a, b) => {
    if (a.status === "Overdue" && b.status !== "Overdue") return -1;
    if (b.status === "Overdue" && a.status !== "Overdue") return 1;
    return String(a.title).localeCompare(String(b.title), undefined, {
      sensitivity: "base",
    });
  });
}

function mergeGateAdvanceSnapshots(snapshots = []) {
  const taskMap = new Map();
  let uploadDone = 0;
  let uploadTotal = 0;
  let signDone = 0;
  let signTotal = 0;
  let overdueCount = 0;

  snapshots.forEach((snapshot) => {
    const kpis = snapshot?.kpis || {};
    uploadDone += Number(kpis.uploadDone) || 0;
    uploadTotal += Number(kpis.uploadTotal) || 0;
    signDone += Number(kpis.signDone) || 0;
    signTotal += Number(kpis.signTotal) || 0;
    overdueCount += Number(kpis.overdueCount) || 0;
    (snapshot?.tasks || []).forEach((task) => {
      if (!task?.id || taskMap.has(task.id)) return;
      taskMap.set(task.id, task);
    });
  });

  return {
    kpis: {
      uploadDone,
      uploadTotal,
      signDone,
      signTotal,
      overdueCount,
      myTasksCount: 0,
    },
    tasks: sortGateAdvanceTasks(Array.from(taskMap.values())),
  };
}

function gateAdvanceUserIds(userData) {
  return {
    organizationId: guidOrNull(userData?.OrganizationId),
    staffId: guidOrNull(userData?.StaffId),
  };
}

function annotateSnapshotTasksWithSiteName(snapshot, siteName) {
  if (!siteName) return snapshot;
  const label = String(siteName).trim();
  if (!label) return snapshot;
  return {
    ...snapshot,
    tasks: (snapshot?.tasks || []).map((task) => {
      const subtitle = String(task?.subtitle || "").trim();
      if (!subtitle || subtitle.includes(label)) {
        return { ...task, subtitle: subtitle || label };
      }
      return { ...task, subtitle: `${subtitle} · ${label}` };
    }),
  };
}

async function loadGateAdvanceStudySiteRows({
  userData,
  studyId,
  templateCategory,
  siteIds = null,
}) {
  const { organizationId, staffId } = gateAdvanceUserIds(userData);
  const category = String(templateCategory || DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY).trim();
  const normalizedStudyId = guidOrNull(studyId);

  try {
    const response = await fetchStudySitesForSetup({
      organizationId,
      studyId: normalizedStudyId,
      templateCategory: category,
      createdBy: staffId,
    });
    const rows = normalizeStudySiteRows(response).filter((row) => row.SiteId);
    if (rows.length) return rows;
  } catch {
    /* fallback below */
  }

  return (siteIds || [])
    .map((site) => ({
      SiteId: guidOrNull(site?.siteId ?? site?.SiteId ?? site?.value ?? site),
      SiteName: String(site?.name ?? site?.SiteName ?? site?.siteName ?? "").trim(),
      TemplateId: guidOrNull(site?.TemplateId ?? site?.templateId) || "",
      TemplateName: String(site?.TemplateName ?? site?.templateName ?? "").trim(),
    }))
    .filter((row) => row.SiteId);
}

async function resolveGateAdvanceTemplateContext({
  userData,
  studyId,
  templateCategory,
  siteRow = null,
  siteIdRef = null,
}) {
  const { organizationId, staffId } = gateAdvanceUserIds(userData);
  const category = String(templateCategory || DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY).trim();
  const normalizedSiteId = guidOrNull(siteRow?.SiteId ?? siteIdRef);
  let templateId = guidOrNull(siteRow?.TemplateId) || "";
  let templateName = String(siteRow?.TemplateName || "").trim();

  if (!templateId) {
    try {
      const src = await fetchStudySourceTemplate({
        organizationId,
        studyId: guidOrNull(studyId),
        templateCategory: category,
        createdBy: staffId,
        siteIdRef: normalizedSiteId,
      });
      templateId = guidOrNull(src?.TemplateId) || "";
      templateName = src?.TemplateName || templateName;
    } catch {
      /* non-blocking */
    }
  }

  return {
    category,
    templateId: guidOrNull(templateId),
    templateName,
    siteIdRef: normalizedSiteId,
  };
}

function mapStaffListRecords(records = []) {
  return records.map((item) => ({
    value: item.id,
    label: item.name,
    email: item.EmailId,
    Role: item.Role,
    roleId: item.RoleId || item.roleId,
  }));
}

async function fetchGateAdvanceStudyBundle({
  userData,
  studyId,
  templateCategory,
  templateId = "",
  templateName = "",
  siteIdRef = null,
}) {
  const { organizationId, staffId } = gateAdvanceUserIds(userData);
  const safeStudyId = guidOrNull(studyId);
  const safeOrgId = guidOrNull(organizationId);
  const safeStaffId = guidOrNull(staffId);
  const safeTemplateId = guidOrNull(templateId);
  const safeSiteId = guidOrNull(siteIdRef);

  if (!safeOrgId || !safeStudyId) {
    return { staffList: [], documents: [] };
  }

  // No template for this category/site (e.g. ePSF never set up) — calling
  // GetStudyFolderDocuments with null TemplateId can hit uniqueidentifier
  // conversion failures inside the SP when folder/site data is incomplete.
  if (!safeTemplateId) {
    return { staffList: [], documents: [] };
  }

  if (!safeStaffId) {
    return { staffList: [], documents: [] };
  }

  const response = await apiClient.post(API_ENDPOINTS.ISF.GET_STUDY_FOLDERS, {
    OrganizationIdRef: safeOrgId,
    ActionType: "GetAllFolder",
    StaudyIdRef: safeStudyId,
    StaffIdRef: safeStaffId,
    TemplateId: safeTemplateId,
    TemplateName: templateName || "",
    TemplateCategory: templateCategory || DEFAULT_GATE_ADVANCE_TEMPLATE_CATEGORY,
    SiteIdRef: safeSiteId,
    FolderIdRef: null,
  });

  const data = normalizeStudyFolderResponseData(response?.data ?? {});
  const staffList = mapStaffListRecords(
    Array.isArray(data.StaffListRecord) ? data.StaffListRecord : [],
  );
  const rawDocs = Array.isArray(data.FolderDocuments) ? data.FolderDocuments : [];
  const documents = consolidateDocumentSignatureRows(rawDocs, staffList);
  return { staffList, documents };
}

function toReadonlyOverviewSnapshot(snapshot) {
  const tasks = (snapshot?.tasks || []).map((task) => ({
    ...task,
    canAct: false,
    isMine: false,
    actionLabel: undefined,
  }));
  return {
    kpis: snapshot?.kpis || {},
    tasks,
  };
}

/** Overview-style open items for phase advance (same rules as site Overview). */
export function buildGateAdvanceOverviewFromPlaceholders(
  placeholders = [],
  {
    documents = [],
    staffList = [],
    roleOptions = [],
    userData = null,
  } = {},
) {
  const snapshot = buildSiteOverviewSnapshot({
    placeholders,
    documents,
    userData,
    staffList,
    roleOptions,
  });
  return toReadonlyOverviewSnapshot(snapshot);
}

async function fetchGateAdvanceOverviewForSite({
  userData,
  studyId,
  gateCode,
  siteRow = null,
  siteIdRef = null,
  templateCategory = "",
  roleOptions = [],
}) {
  const { organizationId, staffId } = gateAdvanceUserIds(userData);
  const templateCtx = await resolveGateAdvanceTemplateContext({
    userData,
    studyId,
    templateCategory,
    siteRow,
    siteIdRef,
  });
  const resolvedSiteId = templateCtx.siteIdRef;

  const [placeholdersRaw, bundle] = await Promise.all([
    resolvedSiteId
      ? fetchStudyPlaceholdersForActiveSite({
          organizationId,
          studyId: guidOrNull(studyId),
          templateCategory: templateCtx.category,
          siteIdRef: resolvedSiteId,
          createdBy: staffId,
          gateCode,
        })
      : fetchStudyPlaceholdersForGates({
          organizationId,
          studyId: guidOrNull(studyId),
          templateCategory: templateCtx.category,
          createdBy: staffId,
          gateCodes: [gateCode],
        }),
    fetchGateAdvanceStudyBundle({
      userData,
      studyId,
      templateCategory: templateCtx.category,
      templateId: templateCtx.templateId,
      templateName: templateCtx.templateName,
      siteIdRef: resolvedSiteId,
    }).catch(() => ({ staffList: [], documents: [] })),
  ]);

  let placeholders = placeholdersRaw || [];
  const documents = enrichDocumentsWithPlaceholderLinks(
    bundle.documents,
    placeholders,
  );
  placeholders = dedupeStudyPlaceholdersForTree(
    augmentPlaceholdersFromDocuments(placeholders, documents),
    documents,
  );

  const snapshot = buildGateAdvanceOverviewFromPlaceholders(placeholders, {
    documents,
    staffList: bundle.staffList,
    roleOptions,
    userData,
  });

  return annotateSnapshotTasksWithSiteName(
    snapshot,
    siteRow?.SiteName || siteRow?.SiteNumber || "",
  );
}

async function resolveGateAdvanceRoleOptions(userData, roleOptions = []) {
  if (Array.isArray(roleOptions) && roleOptions.length) return roleOptions;
  try {
    return (await fetchRoleLookup(userData)) || [];
  } catch {
    return [];
  }
}

export async function fetchGateAdvanceOverview({
  userData,
  studyId,
  gateCode,
  siteIdRef = null,
  siteIds = null,
  templateCategory = "",
  roleOptions = [],
}) {
  if (!userData?.OrganizationId || !studyId || !gateCode) {
    return { kpis: {}, tasks: [] };
  }

  const effectiveRoleOptions = await resolveGateAdvanceRoleOptions(
    userData,
    roleOptions,
  );

  if (siteIdRef) {
    const siteRows = await loadGateAdvanceStudySiteRows({
      userData,
      studyId,
      templateCategory,
      siteIds,
    });
    const siteRow =
      siteRows.find(
        (row) =>
          String(row.SiteId).toLowerCase() ===
          String(guidOrNull(siteIdRef)).toLowerCase(),
      ) || null;
    return fetchGateAdvanceOverviewForSite({
      userData,
      studyId,
      gateCode,
      siteRow,
      siteIdRef: guidOrNull(siteIdRef),
      templateCategory,
      roleOptions: effectiveRoleOptions,
    });
  }

  const siteRows = await loadGateAdvanceStudySiteRows({
    userData,
    studyId,
    templateCategory,
    siteIds,
  });

  if (siteRows.length) {
    const snapshots = await Promise.all(
      siteRows.map((siteRow) =>
        fetchGateAdvanceOverviewForSite({
          userData,
          studyId,
          gateCode,
          siteRow,
          templateCategory,
          roleOptions: effectiveRoleOptions,
        }).catch(() => ({ kpis: {}, tasks: [] })),
      ),
    );
    return mergeGateAdvanceSnapshots(snapshots);
  }

  return fetchGateAdvanceOverviewForSite({
    userData,
    studyId,
    gateCode,
    siteIdRef: null,
    templateCategory,
    roleOptions: effectiveRoleOptions,
  });
}

/** Required placeholders still open in the current study gate (for advance modal). */
export async function fetchGateAdvancePendingItems({
  userData,
  studyId,
  gateCode,
}) {
  if (!userData?.OrganizationId || !studyId || !gateCode) return [];

  const rows = await fetchStudyPlaceholdersForGates({
    organizationId: userData.OrganizationId,
    studyId,
    createdBy: userData.StaffId,
    gateCodes: [gateCode],
  }).catch(() => []);

  return rows
    .filter((row) => {
      const required = row?.IsRequired === 1 || row?.IsRequired === true;
      const status = String(row?.Status ?? row?.status ?? "").trim();
      return required && (status === "Pending" || status === "Overdue");
    })
    .map((row) => ({
      studyPlaceholderId: row.StudyPlaceholderId ?? row.studyPlaceholderId,
      siteId: row.SiteIdRef ?? row.siteIdRef ?? null,
      siteName:
        row.SiteName ??
        row.siteName ??
        (row.SiteIdRef || row.siteIdRef ? "Site" : "Study-wide"),
      milestoneCode: row.MilestoneCode ?? row.milestoneCode ?? "",
      placeholderName:
        row.PlaceholderName ??
        row.placeholderName ??
        row.MilestoneName ??
        row.milestoneName ??
        "",
      folderName: row.FolderName ?? row.folderName ?? "",
      status: row.Status ?? row.status ?? "Pending",
      assignedToName: row.AssignedToName ?? row.assignedToName ?? "",
    }))
    .sort((a, b) => {
      const bySite = String(a.siteName).localeCompare(String(b.siteName));
      if (bySite !== 0) return bySite;
      const byCode = String(a.milestoneCode).localeCompare(String(b.milestoneCode));
      if (byCode !== 0) return byCode;
      return String(a.placeholderName).localeCompare(String(b.placeholderName));
    });
}
